import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  Divider,
  useMediaQuery,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import ThemeToggleButton from "./ThemeToggleButton";
import { useNavigate } from "react-router-dom";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { useGoldRate } from "../context/GoldRateContext";
import ScannerModal from "./ScannerModal";
import GoldRateConfigModal from "./GoldRateConfigModal";
import { useContext } from "react";
import { DataContext } from "../context/DataContext";

const Navbar = ({ children, sidebarOpen = true, onMenuClick, onItemScanned }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDarkMode = theme.palette.mode === "dark";
  const { stock } = useContext(DataContext);
  const { showRateConfig, setShowRateConfig } = useGoldRate();
  const [scannerOpen, setScannerOpen] = useState(false);

  const SIDEBAR_WIDTH_OPEN = 290;
  const SIDEBAR_WIDTH_CLOSED = 90;

  const navbarWidth = !isMobile
    ? sidebarOpen
      ? `calc(100% - ${SIDEBAR_WIDTH_OPEN}px)`
      : `calc(100% - ${SIDEBAR_WIDTH_CLOSED}px)`
    : "100%";

  const navbarLeft = !isMobile
    ? sidebarOpen
      ? `${SIDEBAR_WIDTH_OPEN}px`
      : `${SIDEBAR_WIDTH_CLOSED}px`
    : 0;

  // Transparent glass background matching sidebar
  const glassBackground = isDarkMode
    ? 'rgba(15, 23, 42, 0.4)'
    : 'rgba(255, 255, 255, 0.4)';

  const colors = {
    light: {
      primary: theme.palette.primary.main,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(37, 99, 235, 0.1)',
    },
    dark: {
      primary: theme.palette.primary.main,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      border: 'rgba(148, 163, 184, 0.1)',
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  const handleHome = () => {
    navigate("/");
  };

  const handleMenuClickInternal = () => {
    if (onMenuClick) {
      onMenuClick();
    }
  };

  const handleScanClick = () => {
    const rates = localStorage.getItem("gold_rates");
    if (!rates) {
      setShowRateConfig(true);
    } else {
      setScannerOpen(true);
    }
  };

  const handleItemScanned = (item) => {
    if (onItemScanned) {
      onItemScanned(item);
    }
    setScannerOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: navbarWidth,
          left: navbarLeft,
          zIndex: theme.zIndex.drawer + 1,
          background: glassBackground,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${currentColors.border}`,
          boxShadow: "none",
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
            minHeight: { xs: 56, sm: 64, md: 70 },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
            {/* Menu Button - Mobile only */}
            {isMobile && (
              <IconButton
                onClick={handleMenuClickInternal}
                size="small"
                sx={{
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                  color: currentColors.textPrimary,
                  "&:hover": {
                    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    transform: "scale(1.05)",
                    color: currentColors.primary,
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, cursor: "pointer" }}
              onClick={handleHome}
            >
              <Box
                sx={{
                  width: { xs: 36, sm: 40, md: 48 },
                  height: { xs: 36, sm: 40, md: 48 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${currentColors.primary} 0%, ${theme.palette.primary.dark} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "1.25rem", sm: "1.35rem", md: "1.5rem" },
                  flexShrink: 0,
                  boxShadow: isDarkMode ? "0 2px 8px rgba(96, 165, 250, 0.2)" : "0 2px 8px rgba(37, 99, 235, 0.2)",
                }}
              >
                💎
              </Box>
              {!isMobile && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.2rem", md: "1.35rem" }, color: currentColors.textPrimary, whiteSpace: "nowrap" }}>JewelStock</Typography>
                  <Typography variant="caption" sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" }, display: { xs: "none", sm: "block" }, fontWeight: 600, color: currentColors.textSecondary, textTransform: "uppercase" }}>Billing & Inventory</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1, md: 1.5 }, flexShrink: 0 }}>
            <IconButton
              onClick={handleScanClick}
              title="Scan Item"
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                color: currentColors.textPrimary,
                "&:hover": {
                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                  transform: "translateY(-2px)",
                  color: currentColors.primary
                }
              }}
            >
              <QrCodeScannerIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
            </IconButton>

            {!isMobile && (
              <IconButton
                onClick={handleHome}
                size="medium"
                title="Home"
                sx={{
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                  color: currentColors.textPrimary,
                  "&:hover": {
                    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    transform: "translateY(-2px)",
                    color: currentColors.primary
                  }
                }}
              >
                <HomeIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}

            <ThemeToggleButton />

            {!isMobile && <Divider orientation="vertical" flexItem sx={{ my: 1.5, mx: 0.5, borderColor: currentColors.border }} />}

            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center' }}>{children}</Box>
          </Box>
        </Toolbar>
      </AppBar>

      <ScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onItemScanned={handleItemScanned} stock={stock} />
      <GoldRateConfigModal open={showRateConfig} onComplete={() => setScannerOpen(true)} />
    </>
  );
};

export default Navbar;